// #549: Datei in die Akte (Drive-Ordner eines Objekts) ablegen — z. B. ein im Chat geschicktes PDF,
// das sonst nach einer Woche mit dem Chat gelöscht würde.
import { useEffect, useMemo, useState } from 'react';
import { FolderSimplePlus, WarningCircle } from '@phosphor-icons/react';
import { DialogHuelle, ImmoButton, ObjektPicker, type ObjektPickerOption } from '../immobilien/ui';
import { ladeObjekte, type AktObjekt } from '../../lib/dokumente/useAktenschrank';
import { ladeDateiHoch, ladeOrdnerBaum, type BaumKnoten } from '../../lib/dokumente/aktenschrankApi';
import { flacheOrdner } from '../../lib/dokumente/aktenschrank';
import { OrdnerAuswahl } from '../../pages/admin/dokumente/ablage/OrdnerAuswahl';

const LABEL = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)' } as const;

export function InAkteAblegenDialog({ dateiname, ladeDatei, onSchliessen, onFertig }: {
  dateiname: string;
  ladeDatei: () => Promise<File>;
  onSchliessen: () => void;
  onFertig: (meldung: string) => void;
}) {
  const [objekte, setObjekte] = useState<AktObjekt[]>([]);
  const [objektId, setObjektId] = useState<string | null>(null);
  const [baum, setBaum] = useState<BaumKnoten[] | null>(null);
  const [pfad, setPfad] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => { ladeObjekte().then(setObjekte).catch((e: unknown) => setFehler((e as Error).message)); }, []);
  useEffect(() => {
    setBaum(null); setPfad('');
    if (!objektId) return;
    let lebt = true;
    ladeOrdnerBaum(objektId).then((b) => { if (lebt) setBaum(b); }).catch((e: unknown) => { if (lebt) setFehler((e as Error).message); });
    return () => { lebt = false; };
  }, [objektId]);
  const flach = useMemo(() => (baum ? flacheOrdner(baum) : []), [baum]);
  const idNachPfad = useMemo(() => {
    const m = new Map<string, string>();
    const lauf = (l: readonly BaumKnoten[]) => l.forEach((k) => { m.set(k.pfad, k.gdriveId); lauf(k.kinder); });
    if (baum) lauf(baum);
    return m;
  }, [baum]);
  const optionen: ObjektPickerOption[] = useMemo(() => objekte.map((o) => ({
    id: o.id, label: o.name, nummer: o.nummer, sub: [o.verwaltungsart, o.ort].filter(Boolean).join(' · '), suchtext: `${o.nummer} ${o.ort}`,
    hinweis: o.driveId ? undefined : 'kein Drive-Ordner',
  })), [objekte]);
  const ordnerId = idNachPfad.get(pfad) ?? '';

  const ablegen = async () => {
    if (!ordnerId) return;
    setLaeuft(true); setFehler(null);
    try {
      await ladeDateiHoch(ordnerId, await ladeDatei());
      onFertig(`„${dateiname}“ in der Akte abgelegt`);
    } catch (e) { setFehler((e as Error).message || 'Ablegen fehlgeschlagen'); setLaeuft(false); }
  };

  return (
    <DialogHuelle titel="In Akte ablegen" untertitel={dateiname} icon={FolderSimplePlus} onSchliessen={onSchliessen} laeuft={laeuft}
      fuss={<>
        <ImmoButton art="fuellung" groesse={44} pille={false} onClick={onSchliessen} disabled={laeuft} style={{ flex: 1 }}>Abbrechen</ImmoButton>
        <ImmoButton art="akzent" groesse={44} pille={false} icon={FolderSimplePlus} onClick={() => void ablegen()} disabled={laeuft || !ordnerId} style={{ flex: 1 }}>
          {laeuft ? 'Wird abgelegt …' : 'Ablegen'}
        </ImmoButton>
      </>}>
      <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={LABEL}>Objekt</span>
        <div><ObjektPicker optionen={optionen} wert={objektId} onWahl={setObjektId} /></div>
      </div>
      <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={LABEL}>Zielordner</span>
        <OrdnerAuswahl optionen={flach} wert={pfad} onWahl={setPfad} disabled={!objektId || !baum || laeuft}
          platzhalter={!objektId ? 'Zuerst ein Objekt wählen' : !baum ? 'Ordner werden geladen …' : 'Ordner wählen …'} />
      </div>
      {fehler && (
        <span role="alert" className="flex items-start" style={{ gap: 6, fontSize: 12, color: 'var(--bad)' }}>
          <WarningCircle weight="fill" size={14} style={{ flexShrink: 0, marginTop: 1 }} />{fehler}
        </span>
      )}
    </DialogHuelle>
  );
}
